import { useState } from "react";
import { Bookmark } from "lucide-react";
import { SecureEvidenceForm } from "./SecureEvidenceForm";
import { bandDef } from "../admission/bandDefinitions";

// Mirrors backend ai_advisor.RULES gap thresholds and ACADEMIC_BUCKETS_TO_PORTFOLIO (display only).
export const SLOT_META = {
  aspirational: { label: "Aspirational", bucket: "highly_ambitious", band: "Highly ambitious", range: "grade gap below −0.60" },
  competitive: { label: "Competitive", bucket: "ambitious", band: "Ambitious", range: "grade gap −0.60 to −0.15" },
  realistic: { label: "Realistic", bucket: "target", band: "Target", range: "grade gap −0.15 to +0.50" },
  secure: { label: "Secure", bucket: "likely", band: "Likely", range: "grade gap +0.50 or more" },
};
const SLOT_ORDER = ["aspirational", "competitive", "realistic", "secure"];
const signed = (v) => `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(2)}`;
const has = (p, ...keys) => keys.some((k) => p?.[k] !== undefined && p?.[k] !== null && p?.[k] !== "" && p?.[k] !== false);

// Profile inputs the grade gap uses but which are blank (weights from ai_advisor.RULES).
export function blankGapInputs(profile) {
  const out = [];
  const cur = profile?.curriculum;
  if (cur === "ib" && !has(profile, "ib_maths", "ib_english")) out.push(["subject grades (Maths, English)", 30]);
  if (cur === "a_level" && !has(profile, "a_level_maths", "a_level_english")) out.push(["subject grades (Maths, English)", 30]);
  const notTaking = new Set(profile?.tests_not_taking || []);
  if (!has(profile, "sat", "act") && !notTaking.has("sat_act")) out.push(["an admissions test (SAT/ACT)", 15]);
  if (!has(profile, "ielts", "toefl", "native_english", "english_waived") && !notTaking.has("english")) out.push(["an English test or native-English status", 10]);
  return out;
}

export function shortlistedInSlot(assess, slot, shownIds) {
  return (assess?.items || [])
    .filter((it) => it.row && it.row.academic?.portfolio_slot === slot && !shownIds.includes(it.degree_id))
    .map((it) => it.row);
}

export function SlotHeader({ slot, size, filled }) {
  const m = SLOT_META[slot];
  const left = Math.max(0, size - filled);
  return (
    <div data-testid={`portfolio-slot-header-${slot}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="label-uppercase">{m.label}</span>
      <span className="text-[12px] text-stone-600">
        {filled} of {size} slots filled{left ? ` · ${left} empty` : ""} · Quick Check band: {m.band}
      </span>
    </div>
  );
}

export function ShortlistedTag({ testId }) {
  return (
    <span data-testid={testId} className="mb-1.5 inline-flex items-center gap-1 rounded-full border border-[#4C1D95]/30 bg-[#4C1D95]/10 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-[#4C1D95]">
      <Bookmark className="h-3 w-3" aria-hidden="true" /> Shortlisted
    </span>
  );
}

// Why a slot has no picks, from the real thresholds and the student's own results.
export function SlotEmptyReason({ slot, results, profile, onEdit, onProfileSaved }) {
  const [asking, setAsking] = useState(false);
  const m = SLOT_META[slot];
  const gaps = results.map((r) => r.academic?.gap_score).filter((g) => typeof g === "number");
  const blanks = blankGapInputs(profile);
  let why;
  if (!results.length) why = "There are no matched courses to place in any tier.";
  else if (slot === "secure") {
    const top = gaps.length ? Math.max(...gaps) : null;
    why = `No course reached ${m.band} (${m.range}).${top != null ? ` Your highest grade gap across ${results.length.toLocaleString()} courses is ${signed(top)}.` : ""}`;
    why += blanks.length
      ? ` This is missing evidence, not ineligibility: ${blanks.map((b) => b[0]).join(", ")} ${blanks.length === 1 ? "is" : "are"} blank, and together ${blanks.length === 1 ? "it makes" : "they make"} up ${blanks.reduce((a, b) => a + b[1], 0)}% of the gap. Adding them can move courses up or down.`
      : " Your grades, subject grades, test and English are all entered; none of your matched courses sits far enough below them to reach Likely.";
  } else if (slot === "aspirational") {
    const low = gaps.length ? Math.min(...gaps) : null;
    why = `No matched course is in the ${m.band} band (${m.range})${low != null ? `; your lowest grade gap is ${signed(low)}` : ""}. WUA doesn't fill this tier with other courses.`;
  } else {
    why = `None of your ${results.length.toLocaleString()} matched courses is in the ${m.band} band (${m.range}). WUA doesn't move courses between tiers to fill it.`;
  }
  return (
    <div data-testid={`portfolio-slot-empty-${slot}`} className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-3 text-[13px] text-stone-700 leading-snug">
      {why}
      {slot === "secure" && blanks.length > 0 && onProfileSaved && results.length > 0 && (
        <button type="button" onClick={() => setAsking((a) => !a)} aria-expanded={asking} data-testid="secure-add-evidence"
          className="ml-1 text-[#4C1D95] underline underline-offset-2">
          {asking ? "Hide" : "Add these now"}
        </button>
      )}
      {onEdit && (
        <button type="button" onClick={onEdit} data-testid={`portfolio-slot-edit-${slot}`} className="ml-1 text-[#4C1D95] underline underline-offset-2">
          Edit my answers
        </button>
      )}
      {asking && <SecureEvidenceForm profile={profile} blanks={blanks} onSaved={(p) => { setAsking(false); onProfileSaved(p); }} />}
    </div>
  );
}

export function PortfolioExplainer({ results, profile, subjects, onEdit, shortlist }) {
  const countries = (profile?.preferred_countries || []).filter(Boolean);
  const subj = (subjects || []).filter(Boolean);
  const items = shortlist?.items || [];
  const inTier = items.filter((it) => it.row && SLOT_META[it.row.academic?.portfolio_slot]).length;
  const noTier = items.length - inTier;
  return (
    <div data-testid="portfolio-explainer" className="mb-6 rounded-xl border border-stone-200 bg-white p-4 text-[13px] text-stone-700 leading-relaxed">
      <p>
        <strong>How these picks are made.</strong> WUA chooses them from the{" "}
        <span data-testid="portfolio-considered-count" className="font-mono font-semibold">{results.length.toLocaleString()}</span>{" "}
        courses that match your subjects{subj.length ? ` (${subj.join(", ")})` : ""},{" "}
        {countries.length ? `your ${countries.length} selected ${countries.length === 1 ? "country" : "countries"}` : "any country"} and your other requirements.
        In each tier it takes the highest overall match. They aren't universities you picked, and your shortlist doesn't change them.
      </p>
      <ul data-testid="portfolio-band-map" className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[12px] text-stone-600">
        {SLOT_ORDER.map((s) => (
          <li key={s}><strong className="text-stone-800">{SLOT_META[s].label}</strong> ({{ aspirational: 2, competitive: 3, realistic: 3, secure: 2 }[s]} slots) = Quick Check “{SLOT_META[s].band}” (card label “{bandDef(SLOT_META[s].bucket).pill}”), {SLOT_META[s].range}</li>
        ))}
      </ul>
      {shortlist && items.length > 0 && (
        <p data-testid="portfolio-shortlist-summary" className="mt-2 text-[12px] text-stone-600">
          Your shortlisted courses appear in their own tier, tagged Shortlisted, with unchanged scores.
          {noTier > 0 && ` ${noTier} of your ${items.length} aren't in any tier (ruled out, outside your countries or not yet assessed). See My shortlist.`}
        </p>
      )}
      {onEdit && (
        <button type="button" onClick={onEdit} data-testid="portfolio-edit-answers" className="mt-2 text-[12px] text-[#4C1D95] underline underline-offset-2">
          Edit my answers
        </button>
      )}
    </div>
  );
}
